/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState, useEffect } from 'react';
import { 
  Bell, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  Info, 
  ArrowRight,
  ShieldAlert,
  Volume2,
  BellRing,
  ExternalLink,
  Laptop,
  RefreshCw,
  X,
  Smartphone,
  Radio,
  Sliders,
  Send,
  Check,
  ShieldCheck,
  Copy,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Package,
  Truck,
  Flame,
  Eye,
  Building2,
  FileText,
  AlertOctagon,
  Sparkles
} from 'lucide-react';
import { Client, CostCenter, Folio, PreInvoice, PendingAction, ProcessStage, UserProfile, isClientOperatorUser, shouldHidePricesForUser } from '../types.ts';
import { NotificationService } from '../lib/notificationService.ts';
import { dataService } from '../lib/dataService.ts';
import { PushSubscriptionService, PushSubscriptionPreferences, DEFAULT_PREFERENCES } from '../lib/pushSubscriptionService.ts';
import { fcmService, CriticalPushPayload } from '../lib/fcmService.ts';

interface AlertItem {
  id: string;
  type: 'critical' | 'warning' | 'success' | 'info';
  title: string;
  description: string;
  timestamp: string;
  refId?: string;
  refType?: 'folio' | 'invoice' | 'rework' | 'system';
  stage?: ProcessStage | string;
}

interface AlertsAndNotificationsProps {
  client: Client;
  costCenter: CostCenter | null;
  folios: Folio[];
  preInvoices: PreInvoice[];
  onNavigateToTab: (tab: string, clientSub?: string, refId?: string) => void;
  currentUser?: UserProfile | null;
}

const ITEMS_PER_PAGE = 5;

// Process Phase Definition for Native Testing and Categorization
const PROCESS_PHASES = [
  { key: 'RECOLECCION', label: '1. Recolección en Planta', desc: 'Arribo de chofer y carga en patio', icon: Package, cat: 'GENERAL' as const },
  { key: 'ENTRADA_CEDIS', label: '2. Entrada CEDIS', desc: 'Recepción, pesaje y control de patio', icon: Building2, cat: 'GENERAL' as const },
  { key: 'DECAPADO_TERMICO_QUIMICO', label: '3. Decapado Térmico/Químico', desc: 'Horno de pirolisis y tinas químicas', icon: Flame, cat: 'GENERAL' as const },
  { key: 'DECAPADO_LAVADO', label: '4. Decapado y Lavado', desc: 'Enjuague y neutralización superficial', icon: Sparkles, cat: 'GENERAL' as const },
  { key: 'INSPECCION', label: '5. Inspección de Calidad', desc: 'Revisión dimensional y de acabado', icon: Eye, cat: 'GENERAL' as const },
  { key: 'RETRABAJO', label: '6. Retrabajo Técnico', desc: 'No conformidad por óxido o impurezas', icon: AlertOctagon, cat: 'RETRABAJO' as const },
  { key: 'SALIDA_CEDIS', label: '7. Salida CEDIS / En Ruta', desc: 'Embarque despachado hacia destino', icon: Truck, cat: 'URGENTE' as const },
  { key: 'ENTREGA_PLANTA', label: '8. Entrega en Planta', desc: 'Firma de recibido por centro de costos', icon: CheckCircle2, cat: 'ENTREGA_PLANTA' as const },
  { key: 'DEVOLUCION', label: '9. Devolución en Planta', desc: 'Rechazo o retorno de piezas', icon: ShieldAlert, cat: 'DEVOLUCION' as const },
  { key: 'PREFACTURA', label: '10. Prefacturación', desc: 'Generación de estado de cuenta', icon: FileText, cat: 'PREFACTURA' as const },
];

export default function AlertsAndNotifications({ 
  client, 
  costCenter, 
  folios, 
  preInvoices,
  onNavigateToTab,
  currentUser
}: AlertsAndNotificationsProps) {
  const hidePrices = shouldHidePricesForUser(currentUser) || isClientOperatorUser(currentUser);

  // Native notification permission & Push Subscription state
  const [permission, setPermission] = useState<'granted' | 'denied' | 'default'>(
    NotificationService.getPermission()
  );

  const [isSubscribed, setIsSubscribed] = useState<boolean>(
    PushSubscriptionService.isSubscribedLocally()
  );

  const [prefs, setPrefs] = useState<PushSubscriptionPreferences>(
    PushSubscriptionService.getPreferences()
  );

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [bgPushCountdown, setBgPushCountdown] = useState<number | null>(null);
  const [testActive, setTestActive] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(fcmService.getTokenFromStorage());
  const [fcmCopied, setFcmCopied] = useState(false);
  const [subscriptionSuccessMessage, setSubscriptionSuccessMessage] = useState<string | null>(null);
  const [selectedTestPhase, setSelectedTestPhase] = useState<string>('RETRABAJO');
  const [isSendingPhasePush, setIsSendingPhasePush] = useState(false);

  // Pagination & Filtering state (5 records per page requirement)
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'critical' | 'warning' | 'success' | 'info'>('all');

  const isInsideIframe = typeof window !== 'undefined' && window.self !== window.top;

  // Offline Pending Actions queue subscription state
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);

  useEffect(() => {
    // Ensure service worker is registered & obtain FCM token
    PushSubscriptionService.initServiceWorker();
    
    // Request FCM token on initial mount
    fcmService.requestFCMToken('Personal Operativo', 'OPERADOR', client.id)
      .then(tok => {
        if (tok) {
          setFcmToken(tok);
          setIsSubscribed(true);
        }
      })
      .catch(err => console.warn('[FCM Mount Token Error]:', err));

    const unsub = dataService.subscribeToPendingActions((queue) => {
      setPendingActions(queue);
    });
    return unsub;
  }, [client.id]);

  const handleCopyFcmToken = () => {
    if (!fcmToken) return;
    navigator.clipboard.writeText(fcmToken);
    setFcmCopied(true);
    setTimeout(() => setFcmCopied(false), 2000);
  };

  const handleTogglePushSubscription = async () => {
    setIsSubscribing(true);
    setSubscriptionSuccessMessage(null);
    try {
      if (isSubscribed) {
        await PushSubscriptionService.unsubscribePersonnel();
        setIsSubscribed(false);
        setSubscriptionSuccessMessage('Doble verificación: El dispositivo ha sido dado de baja de las alertas push.');
        setTimeout(() => setSubscriptionSuccessMessage(null), 4000);
      } else {
        await PushSubscriptionService.subscribePersonnel('Personal Operativo', 'OPERADOR', prefs);
        
        // Register with Firebase Cloud Messaging
        const tok = await fcmService.requestFCMToken('Personal Operativo', 'OPERADOR', client.id);
        if (tok) setFcmToken(tok);

        setIsSubscribed(true);
        setPermission('granted');

        const successText = '✓ ¡Dispositivo suscrito con éxito a las Notificaciones Push Nativas de Firebase Cloud Messaging (FCM)! Llegarán a todos los dispositivos con la app descargada.';
        setSubscriptionSuccessMessage(successText);
        setTimeout(() => setSubscriptionSuccessMessage(null), 6000);

        await fcmService.sendCriticalStatusNotification({
          title: '🔔 Dispositivo Registrado - Alertas Push Activas',
          body: `El dispositivo de ${client.name} ha quedado registrado exitosamente para recibir notificaciones nativas de cualquier fase de los procesos.`,
          category: 'GENERAL',
          clientName: client.name
        }).catch(err => console.warn('[Instant FCM notification test error]:', err));
      }
    } catch (err: any) {
      console.error('[Push Subscription Toggle Error]:', err);
      alert(`Información de suscripción: ${err?.message || err}`);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handlePrefToggle = (key: keyof PushSubscriptionPreferences) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    PushSubscriptionService.savePreferences(updated);
  };

  // Trigger Native Push Notification for ANY selected Process Phase
  const handleTriggerPhasePush = async () => {
    setIsSendingPhasePush(true);
    try {
      const phaseObj = PROCESS_PHASES.find(p => p.key === selectedTestPhase) || PROCESS_PHASES[0];
      const testFolioId = folios.find(f => f.clientId === client.id)?.id || 'F-102524';
      
      const payload: CriticalPushPayload = {
        title: `🚨 Fase Operativa: ${phaseObj.label}`,
        body: `Folio ${testFolioId} (${client.name}): ${phaseObj.desc}. Notificación nativa sincronizada en todos los dispositivos.`,
        category: phaseObj.cat,
        folioId: testFolioId,
        clientName: client.name,
        newStatus: phaseObj.key,
        url: '/#trazabilidad'
      };

      await fcmService.sendCriticalStatusNotification(payload);

      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'TRIGGER_PUSH_TEST',
          title: payload.title,
          body: payload.body,
          delayMs: 100,
          targetUrl: '/#trazabilidad'
        });
      }

      setSubscriptionSuccessMessage(`✓ Notificación nativa de fase "${phaseObj.label}" enviada y replicada a todos los dispositivos descargados.`);
      setTimeout(() => setSubscriptionSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('[Phase Push Trigger Error]:', err);
    } finally {
      setIsSendingPhasePush(false);
    }
  };

  const handleTestBackgroundPush = async () => {
    setBgPushCountdown(3);

    // Countdown interval
    let count = 3;
    const interval = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        clearInterval(interval);
        setBgPushCountdown(null);

        // Trigger FCM critical notification via Service Worker / Push
        fcmService.sendCriticalStatusNotification({
          title: '🚨 FCM PUSH SEGUNDO PLANO - RETRABAJO',
          body: 'Atención Personal: Folio F-102524 ha entrado a Retrabajo Técnico por residuos de óxido.',
          category: 'RETRABAJO',
          folioId: 'F-102524',
          clientName: client.name,
          newStatus: 'RETRABAJO'
        });

        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'TRIGGER_PUSH_TEST',
            title: '🚨 FCM PUSH SEGUNDO PLANO - RETRABAJO',
            body: 'Atención Personal: Folio F-102524 ha entrado a Retrabajo Técnico por residuos de óxido.',
            delayMs: 100,
            targetUrl: '/#trazabilidad'
          });
        }
      } else {
        setBgPushCountdown(count);
      }
    }, 1000);
  };

  const handleTestChime = () => {
    setTestActive(true);
    NotificationService.sendNotification(
      '🔊 Prueba de Sonido SIC SNF',
      { body: 'El timbre del sistema de alerta dual-tone funciona correctamente en este dispositivo.' },
      'success'
    );
    setTimeout(() => setTestActive(false), 800);
  };

  // Comprehensive Dynamic Generation of Client-Specific Alerts spanning ALL process phases
  const allAlerts: AlertItem[] = useMemo(() => {
    const list: AlertItem[] = [];

    // Filter folios and pre-invoices for this client
    const clientFolios = folios.filter(f => f.clientId === client.id || f.clientName === client.name);
    const clientInvoices = preInvoices.filter(inv => inv.clientId === client.id || inv.clientName === client.name);

    // 1. Phase: RETRABAJO (Critical)
    clientFolios.filter(f => f.status === 'RETRABAJO').forEach(f => {
      list.push({
        id: `alert-rew-${f.id}`,
        type: 'critical',
        stage: 'RETRABAJO',
        title: `Piezas en Retrabajo Técnico (${f.id})`,
        description: `El folio ${f.id} ha presentado no conformidad en la inspección superficial de calidad y se encuentra bajo retrabajo técnico.`,
        timestamp: 'Hace 10 minutos',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 2. Phase: DEVOLUCION (Critical / Warning)
    clientFolios.filter(f => f.status === 'DEVOLUCION').forEach(f => {
      list.push({
        id: `alert-dev-${f.id}`,
        type: 'critical',
        stage: 'DEVOLUCION',
        title: `Devolución Registrada en Planta (${f.id})`,
        description: `Se ha registrado una devolución o rechazo de carga en el folio ${f.id} por centro de costos destino.`,
        timestamp: 'Hace 30 minutos',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 3. Phase: SALIDA_CEDIS (Warning / En Ruta de Entrega)
    clientFolios.filter(f => f.status === 'SALIDA_CEDIS').forEach(f => {
      list.push({
        id: `alert-out-${f.id}`,
        type: 'warning',
        stage: 'SALIDA_CEDIS',
        title: `Carga Despachada y en Ruta (${f.id})`,
        description: `El camión con el folio ${f.id} ha salido de patio CEDIS y se encuentra en tránsito hacia la planta de entrega.`,
        timestamp: 'Hace 1 hora',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 4. PreInvoices: Draft or Sent (Warning) - Restricted: Hidden for client operator users
    if (!hidePrices) {
      clientInvoices.filter(inv => inv.status === 'Sent' || inv.status === 'Draft').forEach(inv => {
        list.push({
          id: `alert-inv-${inv.id}`,
          type: 'warning',
          title: `Prefactura Pendiente de Autorización (${inv.id})`,
          description: `La prefactura corporativa ${inv.id} con un total de $${inv.totalMXN.toLocaleString()} MXN requiere aprobación para timbrado formal.`,
          timestamp: 'Hace 2 horas',
          refId: inv.id,
          refType: 'invoice'
        });
      });
    }

    // 5. Phase: ENTREGA_PLANTA (Success)
    clientFolios.filter(f => f.status === 'ENTREGA_PLANTA').forEach(f => {
      list.push({
        id: `alert-del-${f.id}`,
        type: 'success',
        stage: 'ENTREGA_PLANTA',
        title: `Entrega Exitosa en Planta (${f.id})`,
        description: `La carga del folio ${f.id} fue entregada y firmada de conformidad por el centro de costos.`,
        timestamp: 'Hoy',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 6. Phase: INSPECCION (Info)
    clientFolios.filter(f => f.status === 'INSPECCION').forEach(f => {
      list.push({
        id: `alert-insp-${f.id}`,
        type: 'info',
        stage: 'INSPECCION',
        title: `Inspección de Calidad en Proceso (${f.id})`,
        description: `El lote asociado al folio ${f.id} se encuentra en revisión óptica y dimensional previa al embarque.`,
        timestamp: 'En curso',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 7. Phase: DECAPADO (Info)
    clientFolios.filter(f => f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO').forEach(f => {
      list.push({
        id: `alert-dec-${f.id}`,
        type: 'info',
        stage: f.status,
        title: `Tratamiento de Decapado y Lavado (${f.id})`,
        description: `El folio ${f.id} está siendo procesado en hornos pirolíticos y tinas químicas de neutralización.`,
        timestamp: 'En estación',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 8. Phase: ENTRADA_CEDIS (Info)
    clientFolios.filter(f => f.status === 'ENTRADA_CEDIS').forEach(f => {
      list.push({
        id: `alert-ced-${f.id}`,
        type: 'info',
        stage: 'ENTRADA_CEDIS',
        title: `Recepción en Patio CEDIS (${f.id})`,
        description: `El folio ${f.id} fue descargado y pesado con éxito en patio logístico.`,
        timestamp: 'Ayer',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 9. Phase: RECOLECCION (Info)
    clientFolios.filter(f => f.status === 'RECOLECCION').forEach(f => {
      list.push({
        id: `alert-rec-${f.id}`,
        type: 'info',
        stage: 'RECOLECCION',
        title: `Recolección Registrada en Origen (${f.id})`,
        description: `El folio ${f.id} ha sido generado y programado para su recolección técnica.`,
        timestamp: 'Reciente',
        refId: f.id,
        refType: 'folio'
      });
    });

    // 10. System information
    list.push({
      id: 'alert-sys-fx',
      type: 'info',
      title: 'Tipo de Cambio FX Sincronizado',
      description: 'El tipo de cambio oficial ha sido actualizado automáticamente a través de la API del Banco Central.',
      timestamp: 'Hoy, 09:00 AM'
    });

    return list;
  }, [folios, preInvoices, client]);

  // Filtered alerts by search and category
  const filteredAlerts = useMemo(() => {
    return allAlerts.filter(alert => {
      // Category match
      if (categoryFilter !== 'all' && alert.type !== categoryFilter) {
        return false;
      }

      // Search match
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const titleMatch = alert.title.toLowerCase().includes(q);
        const descMatch = alert.description.toLowerCase().includes(q);
        const refMatch = (alert.refId || '').toLowerCase().includes(q);
        if (!titleMatch && !descMatch && !refMatch) {
          return false;
        }
      }

      return true;
    });
  }, [allAlerts, categoryFilter, searchTerm]);

  // Pagination calculation (5 records per page)
  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / ITEMS_PER_PAGE));

  // Reset or adjust current page if out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedAlerts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAlerts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAlerts, currentPage]);

  const startIndexDisplay = filteredAlerts.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endIndexDisplay = Math.min(currentPage * ITEMS_PER_PAGE, filteredAlerts.length);

  return (
    <div className="space-y-6" id="alerts-and-notifications-view">
      
      {/* Header Banner */}
      <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-tiny flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-50 text-brand-red rounded-xl relative shrink-0">
            <Bell className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand-red border-2 border-white rounded-full"></span>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 font-display">Centro de Alertas y Notificaciones Nativas</h3>
            <p className="text-xs text-slate-500 mt-0.5">Sigue los reportes críticos de calidad, trazabilidad en todas las fases y facturación.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] bg-red-100 text-brand-red font-mono font-bold px-2.5 py-1 rounded-full border border-red-200">
            {allAlerts.length} Alertas registradas
          </span>
          <span className="text-[10px] bg-slate-100 text-slate-700 font-mono font-bold px-2.5 py-1 rounded-full border border-slate-200">
            5 registros por página
          </span>
        </div>
      </div>

      {/* Real-time Multi-Device Native Notification Control Panel */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 text-white p-6 rounded-xl border border-slate-800 shadow-lg relative overflow-hidden space-y-5">
        {/* Abstract futuristic grid background decoration */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:2rem_2rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-30" />
        
        <div className="relative z-10 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
                <h4 className="text-xs font-bold font-mono text-cyan-400 uppercase tracking-widest">
                  Firebase Cloud Messaging (FCM) • Service Worker Push Nativo
                </h4>
                {fcmToken && (
                  <button
                    onClick={handleCopyFcmToken}
                    className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-300 font-mono px-2 py-0.5 rounded border border-slate-700 cursor-pointer transition-all"
                    title="Copiar Token FCM para envío de notificaciones push en segundo plano"
                  >
                    <Copy className="h-3 w-3 text-cyan-400" />
                    <span>{fcmCopied ? 'Token Copiado!' : `Token FCM: ${fcmToken.substring(0, 14)}...`}</span>
                  </button>
                )}
              </div>
              <h3 className="text-base font-bold font-display text-white">Notificaciones Nativas para Dispositivos Descargados / PWA</h3>
              <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
                Garantía de entrega nativa en móviles (Android / iOS con PWA descargada) y computadoras. Las notificaciones se disparan en cualquier fase de los procesos (Recolección, Entrada CEDIS, Decapado, Inspección, Retrabajo, Salida y Entrega).
              </p>
            </div>

            <div className="shrink-0 flex flex-wrap gap-2.5">
              <button
                onClick={handleTogglePushSubscription}
                disabled={isSubscribing}
                className={`inline-flex items-center gap-2 font-bold text-xs px-4 py-2 rounded-xl cursor-pointer transition-all active:scale-95 shadow-md ${
                  isSubscribed 
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-slate-950' 
                    : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 animate-pulse'
                }`}
              >
                <Smartphone className="h-4 w-4" />
                <span>
                  {isSubscribing 
                    ? 'Procesando...' 
                    : isSubscribed 
                      ? '✓ Dispositivo Subscrito a Push' 
                      : 'Suscribir Dispositivo a Alertas Push'}
                </span>
              </button>

              <button
                onClick={handleTestBackgroundPush}
                disabled={bgPushCountdown !== null}
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-cyan-400 font-bold text-xs px-4 py-2 rounded-xl cursor-pointer transition-all active:scale-95"
              >
                <Radio className={`h-3.5 w-3.5 ${bgPushCountdown !== null ? 'animate-spin text-cyan-400' : ''}`} />
                <span>
                  {bgPushCountdown !== null 
                    ? `Enviando Push en ${bgPushCountdown}s...` 
                    : 'Probar Segundo Plano'}
                </span>
              </button>

              <button
                onClick={handleTestChime}
                disabled={testActive}
                className={`inline-flex items-center gap-1.5 border font-bold text-xs px-3 py-2 rounded-xl cursor-pointer transition-all active:scale-95 ${
                  testActive 
                    ? 'bg-slate-800 border-cyan-400 text-cyan-400' 
                    : 'bg-transparent border-slate-700 hover:border-slate-600 text-slate-200'
                }`}
                title="Probar sintetizador de audio dual-tone"
              >
                <Volume2 className={`h-3.5 w-3.5 ${testActive ? 'animate-bounce text-cyan-400' : ''}`} />
                <span>{testActive ? 'Sonando...' : 'Timbre'}</span>
              </button>
            </div>
          </div>

          {/* Interactive Phase Notification Trigger Section */}
          <div className="p-4 bg-slate-800/90 border border-cyan-500/30 rounded-xl space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="text-xs font-bold text-cyan-300 uppercase tracking-wider font-mono flex items-center gap-1.5">
                <BellRing className="h-3.5 w-3.5 text-cyan-400" />
                Validador y Disparador de Notificaciones Nativas por Fase:
              </span>
              <span className="text-[10px] text-slate-300 font-mono">
                Transmisión a todos los dispositivos registrados
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1">
                <select
                  value={selectedTestPhase}
                  onChange={(e) => setSelectedTestPhase(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-400 focus:outline-hidden"
                >
                  {PROCESS_PHASES.map((phase) => (
                    <option key={phase.key} value={phase.key}>
                      {phase.label} — {phase.desc}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleTriggerPhasePush}
                disabled={isSendingPhasePush}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs px-4 py-2 rounded-lg cursor-pointer transition-all active:scale-95 shadow-md flex items-center justify-center gap-2 shrink-0"
              >
                <Send className={`h-3.5 w-3.5 ${isSendingPhasePush ? 'animate-spin' : ''}`} />
                <span>{isSendingPhasePush ? 'Emitiendo...' : 'Disparar Notificación de Fase'}</span>
              </button>
            </div>
          </div>

          {subscriptionSuccessMessage && (
            <div className="p-3 bg-emerald-950/80 border border-emerald-500/60 rounded-xl text-emerald-200 text-xs font-medium flex items-center justify-between gap-2 animate-fadeIn">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                <span>{subscriptionSuccessMessage}</span>
              </div>
              <button 
                onClick={() => setSubscriptionSuccessMessage(null)}
                className="text-emerald-400 hover:text-white text-xs font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* Subscription Topics & Categories Filter Grid */}
          <div className="p-4 bg-slate-800/80 border border-slate-700/80 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-200 uppercase tracking-wider font-mono flex items-center gap-1.5">
                <Sliders className="h-3.5 w-3.5 text-cyan-400" />
                Canales de Suscripción para el Personal Operativo:
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {isSubscribed ? 'Suscripción Activa en Firestore' : 'No Subscrito (Habilita arriba)'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
              <label 
                onClick={() => handlePrefToggle('criticalQuality')}
                className={`p-2.5 rounded-lg border flex items-center gap-2.5 cursor-pointer transition-all ${
                  prefs.criticalQuality 
                    ? 'bg-red-950/40 border-red-500/50 text-red-200' 
                    : 'bg-slate-900/40 border-slate-700 text-slate-500'
                }`}
              >
                <input 
                  type="checkbox" 
                  checked={prefs.criticalQuality} 
                  onChange={() => {}} 
                  className="rounded text-red-500 focus:ring-0 cursor-pointer"
                />
                <div>
                  <p className="font-bold text-[11px]">🚨 Calidad y Retrabajos</p>
                  <p className="text-[9px] text-slate-400">Inspección fallida y piezas bajo reproceso</p>
                </div>
              </label>

              <label 
                onClick={() => handlePrefToggle('logisticsDelivered')}
                className={`p-2.5 rounded-lg border flex items-center gap-2.5 cursor-pointer transition-all ${
                  prefs.logisticsDelivered 
                    ? 'bg-cyan-950/40 border-cyan-500/50 text-cyan-200' 
                    : 'bg-slate-900/40 border-slate-700 text-slate-500'
                }`}
              >
                <input 
                  type="checkbox" 
                  checked={prefs.logisticsDelivered} 
                  onChange={() => {}} 
                  className="rounded text-cyan-500 focus:ring-0 cursor-pointer"
                />
                <div>
                  <p className="font-bold text-[11px]">🚚 Logística y Fletes en Ruta</p>
                  <p className="text-[9px] text-slate-400">Salidas CEDIS y entregas en planta</p>
                </div>
              </label>

              {!hidePrices && (
                <label 
                  onClick={() => handlePrefToggle('billingApprovals')}
                  className={`p-2.5 rounded-lg border flex items-center gap-2.5 cursor-pointer transition-all ${
                    prefs.billingApprovals 
                      ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-200' 
                      : 'bg-slate-900/40 border-slate-700 text-slate-500'
                  }`}
                >
                  <input 
                    type="checkbox" 
                    checked={prefs.billingApprovals} 
                    onChange={() => {}} 
                    className="rounded text-emerald-500 focus:ring-0 cursor-pointer"
                  />
                  <div>
                    <p className="font-bold text-[11px]">📄 Prefacturas y Cobranza</p>
                    <p className="text-[9px] text-slate-400">Aprobaciones y timbrado fiscal de cobros</p>
                  </div>
                </label>
              )}

              <label 
                onClick={() => handlePrefToggle('emergencySystem')}
                className={`p-2.5 rounded-lg border flex items-center gap-2.5 cursor-pointer transition-all ${
                  prefs.emergencySystem 
                    ? 'bg-amber-950/40 border-amber-500/50 text-amber-200' 
                    : 'bg-slate-900/40 border-slate-700 text-slate-500'
                }`}
              >
                <input 
                  type="checkbox" 
                  checked={prefs.emergencySystem} 
                  onChange={() => {}} 
                  className="rounded text-amber-500 focus:ring-0 cursor-pointer"
                />
                <div>
                  <p className="font-bold text-[11px]">⚠️ Avisos Generales y Fases</p>
                  <p className="text-[9px] text-slate-400">Recolección, CEDIS y decapado</p>
                </div>
              </label>
            </div>
          </div>

          {/* Special iframe / sandbox notice block */}
          {isInsideIframe && (
            <div className="bg-slate-800/60 border border-slate-700/80 p-3 rounded-lg text-[11px] text-slate-300 space-y-1">
              <div className="flex items-center gap-1.5 text-amber-400 font-bold font-mono">
                <Laptop className="h-3.5 w-3.5 shrink-0" />
                <span>NOTA DE DISPOSITIVO / NAVEGADOR</span>
              </div>
              <p className="leading-relaxed">
                Si ejecutas el portal en modo vista previa, abre la aplicación en una pestaña nueva o instálala como PWA desde el menú del navegador para recibir notificaciones push en segundo plano incluso con el navegador cerrado.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Offline Pending Actions visual Queue */}
      {pendingActions.length > 0 && (
        <div className="bg-white p-5 rounded-xl border border-amber-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                <RefreshCw className="h-4.5 w-4.5 animate-spin-slow" />
              </div>
              <div>
                <h3 className="font-bold text-xs uppercase tracking-tight text-slate-900 font-sans">Cola de Acciones Pendientes ({pendingActions.length})</h3>
                <p className="text-[10px] text-slate-500 mt-0.5">Operaciones fuera de línea esperando sincronización automática.</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  if (window.confirm('¿Deseas descartar todas las acciones pendientes de sincronización?')) {
                    localStorage.setItem('snfsic_pending_actions', JSON.stringify([]));
                    setPendingActions([]);
                  }
                }}
                className="px-2.5 py-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
              >
                Descartar Todo
              </button>
              <button
                onClick={async () => {
                  await dataService.syncPendingActions();
                }}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1 shadow-xs"
              >
                <RefreshCw className="h-3.5 w-3.5 animate-spin-slow text-slate-950" />
                Sincronizar ahora
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[200px] overflow-y-auto custom-scrollbar">
            {pendingActions.map((item) => (
              <div 
                key={item.id} 
                className="p-3.5 rounded-xl border border-slate-100 bg-slate-50/50 flex items-start justify-between gap-3 group hover:border-slate-200 transition-colors"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded-xs text-[8px] font-black tracking-wider uppercase font-mono ${
                      item.action === 'save' 
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                        : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {item.action === 'save' ? 'Guardar' : 'Eliminar'}
                    </span>
                    <span className="text-[8px] text-slate-400 font-mono">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-800 leading-snug">
                    {item.description}
                  </p>
                  <p className="text-[9px] text-slate-400 font-mono">
                    Colección: {item.collectionName}
                  </p>
                </div>
                
                <button
                  onClick={() => dataService.removePendingAction(item.id)}
                  className="text-slate-400 hover:text-red-500 p-1 rounded-lg hover:bg-slate-100 transition-all opacity-0 group-hover:opacity-100 cursor-pointer shrink-0"
                  title="Eliminar de la cola"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter and Search Bar for Alerts */}
      <div className="bg-white p-4 rounded-xl border border-slate-200/90 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por folio, cliente, tipo de fase o descripción..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 font-medium placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-red-500/20 focus:border-brand-red transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
            >
              ✕
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => { setCategoryFilter('all'); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
              categoryFilter === 'all'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todas ({allAlerts.length})
          </button>
          <button
            onClick={() => { setCategoryFilter('critical'); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1 ${
              categoryFilter === 'critical'
                ? 'bg-red-600 text-white shadow-xs'
                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
            }`}
          >
            <ShieldAlert className="h-3 w-3" />
            Críticas ({allAlerts.filter(a => a.type === 'critical').length})
          </button>
          <button
            onClick={() => { setCategoryFilter('warning'); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1 ${
              categoryFilter === 'warning'
                ? 'bg-amber-600 text-white shadow-xs'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
            }`}
          >
            <AlertTriangle className="h-3 w-3" />
            Avisos ({allAlerts.filter(a => a.type === 'warning').length})
          </button>
          <button
            onClick={() => { setCategoryFilter('success'); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1 ${
              categoryFilter === 'success'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            <CheckCircle2 className="h-3 w-3" />
            Éxito ({allAlerts.filter(a => a.type === 'success').length})
          </button>
          <button
            onClick={() => { setCategoryFilter('info'); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1 ${
              categoryFilter === 'info'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
            }`}
          >
            <Info className="h-3 w-3" />
            Info ({allAlerts.filter(a => a.type === 'info').length})
          </button>
        </div>
      </div>

      {/* Alerts Stack with Scrollbar & Height Constraint to avoid stretching the page downwards */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-4">
        
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-brand-red" />
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
              Listado de Alertas y Avisos
            </h4>
          </div>
          <span className="text-[11px] text-slate-500 font-mono">
            Mostrando {startIndexDisplay} a {endIndexDisplay} de {filteredAlerts.length} registros
          </span>
        </div>

        {/* Scrollable Container with max-height to prevent stretching down the page */}
        <div className="max-h-[460px] overflow-y-auto custom-scrollbar smooth-scroll space-y-3 pr-1.5">
          {paginatedAlerts.length === 0 ? (
            <div className="p-10 text-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              <h4 className="text-xs font-bold text-slate-800">No se encontraron alertas en este criterio</h4>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {searchTerm ? 'Intenta modificar el término de búsqueda o restablecer el filtro.' : '¡Tu operación se encuentra completamente al día!'}
              </p>
            </div>
          ) : (
            paginatedAlerts.map((alert) => {
              const isCritical = alert.type === 'critical';
              const isWarning = alert.type === 'warning';
              const isSuccess = alert.type === 'success';
              
              return (
                <div 
                  key={alert.id}
                  className={`p-4 rounded-xl border transition-all flex items-start gap-3.5 bg-white hover:shadow-xs ${
                    isCritical ? 'border-red-200 hover:border-red-300 bg-red-50/20' :
                    isWarning ? 'border-amber-200 hover:border-amber-300 bg-amber-50/20' :
                    isSuccess ? 'border-emerald-200 hover:border-emerald-300 bg-emerald-50/20' : 
                    'border-slate-200 hover:border-slate-300 bg-slate-50/20'
                  }`}
                >
                  {/* Visual Icon Badge */}
                  <div className={`p-2 rounded-xl shrink-0 mt-0.5 ${
                    isCritical ? 'bg-red-50 text-red-600 border border-red-200' :
                    isWarning ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                    isSuccess ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 
                    'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>
                    {isCritical ? <ShieldAlert className="h-4.5 w-4.5 animate-bounce" /> :
                     isWarning ? <AlertTriangle className="h-4.5 w-4.5" /> :
                     isSuccess ? <CheckCircle2 className="h-4.5 w-4.5" /> : <Info className="h-4.5 w-4.5" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-xs font-bold text-slate-900">{alert.title}</h4>
                        {alert.stage && (
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold font-mono ${
                            alert.stage === 'RETRABAJO' || alert.stage === 'DEVOLUCION'
                              ? 'bg-red-100 text-red-800 border border-red-300'
                              : alert.stage === 'SALIDA_CEDIS'
                                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                : alert.stage === 'ENTREGA_PLANTA'
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                  : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}>
                            {alert.stage.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono font-medium shrink-0">{alert.timestamp}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                      {alert.description}
                    </p>

                    {/* Quick Action link */}
                    {alert.refId && (
                      <button
                        onClick={() => {
                          if (alert.refType === 'folio' || alert.stage) {
                            onNavigateToTab('trazabilidad', client.id, alert.refId);
                          } else if (alert.refType === 'invoice') {
                            onNavigateToTab('facturas_cobros', client.id, alert.refId);
                          }
                        }}
                        className="inline-flex items-center gap-1 text-[10px] text-brand-red hover:underline font-bold mt-2 cursor-pointer"
                      >
                        <span>Ir al detalle del registro ({alert.refId})</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Toolbar (5 Records per page) */}
        {filteredAlerts.length > 0 && (
          <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-[11px] text-slate-500 font-medium">
              Página <strong className="text-slate-800">{currentPage}</strong> de <strong className="text-slate-800">{totalPages}</strong> (5 alertas por página)
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1 cursor-pointer"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span>Anterior</span>
              </button>

              {/* Numbered Page Buttons */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                  // Show current, first, last, and immediate neighbors if many pages
                  if (
                    totalPages > 7 &&
                    pageNum !== 1 &&
                    pageNum !== totalPages &&
                    Math.abs(pageNum - currentPage) > 1
                  ) {
                    if (pageNum === 2 || pageNum === totalPages - 1) {
                      return <span key={pageNum} className="text-slate-400 text-xs px-1">...</span>;
                    }
                    return null;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`h-7 w-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        currentPage === pageNum
                          ? 'bg-brand-red text-white shadow-2xs'
                          : 'text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1 cursor-pointer"
              >
                <span>Siguiente</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

      </div>

    </div>
  );
}
